import { useCallback, useEffect, useMemo, useState } from 'react'

import { API_BASE } from '../../config'
import { ApiError } from '../../lib/api'
import type { Edit, FormBundle, FormRow, HoursHint, LeaveTypeOption } from '../../lib/approvals'
import { APPROVALS_BASE, useApprovals } from '../../lib/approvals'
import { errorText } from '../../lib/offsite'

/* ============================================================================
   แผงตรวจ / แก้ / อนุมัติ ใบเดียว
   API: backend/src/routes/adminApprovals.js

   ── ทำไมต้องแก้ได้ในใบเดิม ────────────────────────────────────────────────
   พนักงานกรอกวัน/เวลา/ประเภทผิดเป็นเรื่องปกติ ถ้า HR ทำได้แค่ "ไม่อนุมัติ"
   ใบต้องกลับไปเริ่มที่ศูนย์: พนักงานยื่นใหม่ → หัวหน้าชั้น 1 → ชั้น 2 → HR อีกรอบ
   ซึ่งเสียเวลาเป็นวัน ๆ ทั้งที่ผิดแค่ตัวเลขเดียว

   ── สิ่งที่ต้องระวัง (เขียนไว้ให้คนที่มาแก้ต่อ) ────────────────────────────
   ⚠️ เวลาทุกช่องเป็น **เวลาไทย** ตรง ๆ ไม่ใช่ UTC — ใช้ <input type="datetime-local">
      ซึ่งไม่มี timezone ในตัวมันเอง จึงส่งค่าที่พิมพ์ขึ้นไปได้ตรง ๆ
      ⛔ ห้ามเอาค่าไป new Date() แล้ว toISOString() จะเลื่อนไป -7 ชม.
   ⚠️ เว้นช่องชั่วโมงว่าง = ให้ backend คิดจากกะจริงให้ (สูตรพอร์ตจากระบบเดิม)
      ใส่เลขเอง = ใช้เลขนั้น — HR เป็นคนตัดสินสุดท้าย ไม่ใช่สูตร
   ⚠️ ปุ่มอนุมัติโผล่จาก `form.actionable` ที่ backend ส่งมา ห้ามคิดเองจาก stage
   ============================================================================ */

/** โทนสีจาก backend → คลาสชิปที่มีอยู่แล้วใน index.css */
export function toneClass(tone: string): string {
  if (tone === 'ok') return 'chip chip--ok'
  if (tone === 'wait') return 'chip chip--warn'
  if (tone === 'bad') return 'chip chip--bad'
  return 'chip chip--off'
}

/** 'YYYY-MM-DDTHH:mm:ss' → 'YYYY-MM-DDTHH:mm' ที่ input datetime-local รับได้ */
const toInput = (iso: string | null) => (iso ? iso.slice(0, 16) : '')

/* ⚠️ ต้องเป็นค่าคงที่นอกคอมโพเนนต์ — เขียน `bundle?.rows ?? []` ตรง ๆ จะได้ array
   ใหม่ทุกครั้งที่ render ทำให้ useMemo ที่ขึ้นกับมันคิดใหม่ทุกรอบ (oxlint จับได้) */
const NO_ROWS: FormRow[] = []

type Draft = {
  dateStart: string
  dateEnd: string
  leaveType: string
  reason: string
  /** ว่าง = ให้ backend คิดให้ */
  hours: string
  remove: boolean
}

const draftOf = (r: FormRow): Draft => ({
  dateStart: toInput(r.dateStart),
  dateEnd: toInput(r.dateEnd),
  leaveType: r.leaveType,
  reason: r.reason ?? '',
  hours: r.hours === null ? '' : String(r.hours),
  remove: false,
})

const same = (a: Draft, b: Draft) =>
  a.dateStart === b.dateStart &&
  a.dateEnd === b.dateEnd &&
  a.leaveType === b.leaveType &&
  a.reason === b.reason &&
  a.hours === b.hours &&
  a.remove === b.remove

export function FormDrawer({
  rowId,
  writable,
  onClose,
  onDone,
}: {
  rowId: string
  writable: boolean
  onClose: () => void
  onDone: (message: string) => void
}) {
  const api = useApprovals()

  const [bundle, setBundle] = useState<FormBundle | null>(null)
  const [types, setTypes] = useState<LeaveTypeOption[]>([])
  const [drafts, setDrafts] = useState<Record<number, Draft>>({})
  const [base, setBase] = useState<Record<number, Draft>>({})
  const [hints, setHints] = useState<Record<number, HoursHint>>({})

  const [note, setNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /* ต้องยืนยันซ้ำก่อนแก้ข้อมูลของรอบที่ปิดแล้ว — เก็บว่ากำลังรอยืนยันอะไรอยู่
     (backend ตอบ 409 มาเป็นด่าน ไม่ใช่หน้าจอเป็นคนตัดสิน) */
  const [needConfirm, setNeedConfirm] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const got = await api.form(rowId)
      setBundle(got)
      const d = Object.fromEntries(got.rows.map((r) => [r.recId, draftOf(r)]))
      setDrafts(d)
      setBase(d)
      setHints({})
      setError(null)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [api, rowId])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    api
      .leaveTypes()
      .then((res) => setTypes(res.types))
      // เลือกประเภทไม่ได้ แต่แก้ช่องอื่นยังได้ — ไม่ต้องทำให้ทั้งแผงพัง
      .catch(() => setTypes([]))
  }, [api])

  const form = bundle?.form ?? null
  const rows = bundle?.rows ?? NO_ROWS

  /** แถวที่ถูกแก้จริง — ส่งขึ้นไปแค่นี้ ไม่ส่งทั้งใบ */
  const changed = useMemo(
    () => rows.filter((r) => base[r.recId] && drafts[r.recId] && !same(base[r.recId], drafts[r.recId])),
    [rows, base, drafts],
  )

  function set(recId: number, patch: Partial<Draft>) {
    setDrafts((prev) => ({ ...prev, [recId]: { ...prev[recId], ...patch } }))
  }

  async function preview(r: FormRow) {
    const d = drafts[r.recId]
    try {
      const hint = await api.hours(rowId, {
        leaveType: d.leaveType,
        dateStart: d.dateStart || null,
        dateEnd: d.dateEnd || null,
      })
      setHints((prev) => ({ ...prev, [r.recId]: hint }))
      set(r.recId, { hours: String(hint.hours) })
    } catch (err) {
      setError(errorText(err))
    }
  }

  async function save(confirmClosedRound = false) {
    if (changed.length === 0) return
    setBusy(true)
    setError(null)
    try {
      const edits: Edit[] = changed.map((r) => {
        const d = drafts[r.recId]
        if (d.remove) return { recId: r.recId, remove: true }
        return {
          recId: r.recId,
          dateStart: d.dateStart || null,
          dateEnd: d.dateEnd || null,
          leaveType: d.leaveType,
          reason: d.reason,
          // ว่าง = ไม่ส่ง → backend คิดจากกะให้
          ...(d.hours === '' ? {} : { hours: Number(d.hours) }),
        }
      })

      const res = await api.edit(rowId, { edits, note: note.trim(), confirmClosedRound })
      setNeedConfirm(null)
      onDone(`แก้ใบ ${rowId} แล้ว ${res.affected} แถว`)
    } catch (err) {
      // 409 = ต้องยืนยัน (รอบปิด) หรือมีคนแก้ไปก่อน — ข้อความจาก backend อธิบายเอง
      if (err instanceof ApiError && err.status === 409) setNeedConfirm(errorText(err))
      else setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function act(action: 'APPROVE' | 'REJECT') {
    setBusy(true)
    setError(null)
    try {
      // ใบที่ค้างชั้นหัวหน้า → act-chain (backend เช็คว่าผู้กดเป็นหัวหน้าชั้นนั้นจริง)
      // ใบที่ถึงมือ HR แล้ว → act (ชั้น HR ชั้นสุดท้าย)
      const isChain = form?.stage === 'wait_chain'
      if (isChain) {
        const res = await api.actChain(rowId, action, note.trim() || undefined)
        onDone(
          action === 'APPROVE'
            ? `อนุมัติใบ ${rowId} (ชั้นหัวหน้า ${res.level}) แล้ว — ใบวิ่งต่อตามสาย`
            : `ไม่อนุมัติใบ ${rowId} แล้ว`,
        )
      } else {
        const res = await api.act(rowId, action, note.trim() || undefined)
        onDone(
          action === 'APPROVE'
            ? `อนุมัติใบ ${rowId} แล้ว (${res.rows} แถว) — เวลาทำงานของวันนั้นถูกคิดใหม่ให้แล้ว`
            : `ไม่อนุมัติใบ ${rowId} แล้ว`,
        )
      }
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function cancel() {
    if (!window.confirm(`ยืนยันยกเลิกใบ ${rowId}?`)) return
    setBusy(true)
    setError(null)
    try {
      const res = await api.cancel(rowId, note.trim() || undefined)
      onDone(
        res.wasClosed
          ? `ยกเลิกใบ ${rowId} แล้ว — ใบนี้เคยอนุมัติ เวลาทำงานของวันนั้นถูกคิดใหม่ให้แล้ว`
          : `ยกเลิกใบ ${rowId} แล้ว`,
      )
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  async function viewEvidence() {
    setError(null)
    try {
      const res = await fetch(`${API_BASE}${APPROVALS_BASE}/forms/${encodeURIComponent(rowId)}/evidence`, {
        credentials: 'include',
      })
      if (!res.ok) throw new Error(`เปิดไฟล์ไม่ได้ (HTTP ${res.status})`)
      const url = URL.createObjectURL(await res.blob())
      window.open(url, '_blank', 'noopener')
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    } catch (err) {
      setError(errorText(err))
    }
  }

  const dirty = changed.length > 0
  const canSave = writable && dirty && note.trim().length > 0 && !busy

  return (
    <>
      {/* ฉากหลังต้อง unmount จริงตอนปิด — index.css ใช้ body:has(.drawer-scrim) ล็อกการเลื่อน */}
      <button type="button" className="drawer-scrim" onClick={onClose} aria-label="ปิดแผง" />

      <aside className="drawer" role="dialog" aria-modal="true" aria-label={`ใบ ${rowId}`}>
        <header className="drawer-head">
          <div>
            <p className="drawer-title">{form ? (form.empName ?? form.empCode) : rowId}</p>
            <p className="drawer-sub">
              {rowId}
              {form && ` · ${form.typeDesc} · ${form.firstDay}${form.firstDay === form.lastDay ? '' : ` ถึง ${form.lastDay}`}`}
            </p>
          </div>
          <button type="button" className="drawer-close" onClick={onClose} aria-label="ปิด">
            ✕
          </button>
        </header>

        <div className="drawer-body">
          {loading && <p className="meta">กำลังโหลด…</p>}
          {error && <p className="error">{error}</p>}

          {form && (
            <>
              <p>
                <span className={toneClass(form.stageTone)}>{form.stageText}</span>{' '}
                <span className="meta">
                  รวม {form.totalHours} ชั่วโมง · {rows.length} แถว
                </span>
              </p>

              {/* สายอนุมัติ: ต้องเห็นว่าใครกดไปแล้ว ไม่ใช่เห็นแต่สถานะรวม */}
              <dl>
                <dt>หัวหน้าชั้น 1</dt>
                <dd>
                  {form.chain.approve1.by
                    ? `${form.chain.approve1.name ?? form.chain.approve1.by} — ${form.chain.approve1.done ? `อนุมัติแล้ว ${form.chain.approve1.at?.slice(0, 16) ?? ''}` : 'ยังไม่กด'}`
                    : 'ไม่ได้กำหนด'}
                </dd>
                <dt>หัวหน้าชั้น 2</dt>
                <dd>
                  {form.chain.approve2.by
                    ? `${form.chain.approve2.name ?? form.chain.approve2.by} — ${form.chain.approve2.done ? `อนุมัติแล้ว ${form.chain.approve2.at?.slice(0, 16) ?? ''}` : 'ยังไม่กด'}`
                    : 'ไม่ได้กำหนด'}
                </dd>
                <dt>HR</dt>
                <dd>
                  {form.chain.hr.by
                    ? `${form.chain.hr.name ?? form.chain.hr.by} — ${form.chain.hr.status ?? ''} ${form.chain.hr.at?.slice(0, 16) ?? ''}`
                    : 'ยังไม่ถึงมือ HR'}
                </dd>
              </dl>

              {form.hasEvidence && (
                <p>
                  <button type="button" className="ghost" onClick={() => void viewEvidence()}>
                    ดูไฟล์แนบหลักฐาน
                  </button>
                </p>
              )}

              {form.chainAlreadyApproved && form.editable && (
                <p className="warn">
                  หัวหน้าอนุมัติใบนี้ไปแล้ว — ถ้าแก้ตัวเลข ข้อมูลจะ<strong>ต่างจากที่หัวหน้าเห็นตอนกด</strong>
                  เหตุผลที่ใส่จะถูกเก็บไว้เป็นหลักฐานว่าแก้อะไรเพราะอะไร
                </p>
              )}
              {form.mixedTypes && (
                <p className="warn">ใบนี้มีหลายประเภทปนกันในเลขที่เดียว — ตรวจให้ดีก่อนอนุมัติ</p>
              )}
              {!form.editable && (
                <p className="meta">
                  ใบนี้แก้ไม่ได้แล้ว ({form.stageText}) — ถ้าต้องแก้ต้องยกเลิกใบแล้วให้ยื่นใหม่
                </p>
              )}

              <h3>รายวันในใบนี้</h3>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>ประเภท</th>
                      <th className="nowrap">เริ่ม</th>
                      <th className="nowrap">สิ้นสุด</th>
                      <th className="num">ชั่วโมง</th>
                      <th>เหตุผลของพนักงาน</th>
                      {writable && form.editable && <th>ลบวัน</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const d = drafts[r.recId]
                      if (!d) return null
                      const editable = writable && form.editable && !d.remove
                      const hint = hints[r.recId]
                      return (
                        <tr key={r.recId} className={d.remove ? 'off' : undefined}>
                          <td>
                            {editable ? (
                              <select
                                value={d.leaveType}
                                onChange={(e) => set(r.recId, { leaveType: e.target.value })}
                              >
                                {/* ประเภทเดิมต้องมีในรายการเสมอ แม้จะถูกลบจาก master ไปแล้ว
                                    ไม่งั้น select จะเด้งไปประเภทอื่นเองโดยไม่มีใครสั่ง */}
                                {!types.some((t) => t.leaveType === d.leaveType) && (
                                  <option value={d.leaveType}>{r.typeDesc}</option>
                                )}
                                {types.map((t) => (
                                  <option key={t.leaveType} value={t.leaveType}>
                                    {t.desc}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              r.typeDesc
                            )}
                          </td>
                          <td className="nowrap">
                            {editable ? (
                              <input
                                type="datetime-local"
                                value={d.dateStart}
                                onChange={(e) => set(r.recId, { dateStart: e.target.value })}
                              />
                            ) : (
                              (r.dateStart?.slice(0, 16) ?? `(${r.workDate ?? '-'})`)
                            )}
                          </td>
                          <td className="nowrap">
                            {editable ? (
                              <input
                                type="datetime-local"
                                value={d.dateEnd}
                                onChange={(e) => set(r.recId, { dateEnd: e.target.value })}
                              />
                            ) : (
                              (r.dateEnd?.slice(0, 16) ?? '-')
                            )}
                          </td>
                          <td className="num nowrap">
                            {editable ? (
                              <>
                                <input
                                  className="hours"
                                  inputMode="decimal"
                                  value={d.hours}
                                  placeholder="คิดให้"
                                  onChange={(e) => set(r.recId, { hours: e.target.value })}
                                />
                                <button
                                  type="button"
                                  className="ghost"
                                  onClick={() => void preview(r)}
                                  title="คิดชั่วโมงจากกะจริงของพนักงาน"
                                >
                                  คิดให้
                                </button>
                                {hint && (
                                  <div className="meta">
                                    {hint.basis === 'no-shift'
                                      ? 'ไม่มีข้อมูลกะของวันนี้ — ต้องใส่เอง'
                                      : hint.basis === 'span'
                                        ? 'ใบปรับปรุงเวลา: นับช่วงเวลาดิบ ไม่หักพัก'
                                        : `วันทำงาน ${hint.workDays} วัน${hint.holidayDays > 0 ? ` · ข้ามวันหยุด ${hint.holidayDays} วัน` : ''}`}
                                  </div>
                                )}
                              </>
                            ) : (
                              (r.hours ?? '-')
                            )}
                          </td>
                          <td>
                            {editable ? (
                              <input value={d.reason} onChange={(e) => set(r.recId, { reason: e.target.value })} />
                            ) : (
                              (r.reason ?? '-')
                            )}
                          </td>
                          {writable && form.editable && (
                            <td>
                              <label className="check">
                                <input
                                  type="checkbox"
                                  checked={d.remove}
                                  onChange={(e) => set(r.recId, { remove: e.target.checked })}
                                />
                                ลบ
                              </label>
                            </td>
                          )}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {writable && (
                <label>
                  เหตุผล / บันทึกของ HR
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={dirty ? 'บังคับกรอกเมื่อแก้ข้อมูลของพนักงาน' : 'ใส่ก็ได้ ไม่ใส่ก็ได้'}
                  />
                  <small className="hint">
                    เก็บลงประวัติของเราเสมอ และเขียนลงช่องหมายเหตุของ HR ในระบบเดิมด้วย
                  </small>
                </label>
              )}

              {needConfirm && (
                <div className="warn">
                  <p>{needConfirm}</p>
                  <button type="button" onClick={() => void save(true)} disabled={busy}>
                    ยืนยัน แก้ต่อไป
                  </button>{' '}
                  <button type="button" className="ghost" onClick={() => setNeedConfirm(null)}>
                    ไม่แก้
                  </button>
                </div>
              )}

              {bundle && bundle.audit.length > 0 && (
                <>
                  <h3>ประวัติจากหลังบ้านนี้</h3>
                  <ul className="todo-list">
                    {bundle.audit.map((a, i) => (
                      <li key={`${a.at}-${i}`}>
                        <span>
                          {a.action === 'edit' ? 'แก้ไข' : a.action === 'approve' ? 'อนุมัติ' : 'ไม่อนุมัติ'} โดย{' '}
                          {a.actorUser}
                          {a.affected === 0 && ' (ไม่มีผล)'}
                        </span>
                        <span className="meta">
                          {a.at.slice(0, 16).replace('T', ' ')} {a.note ? `· ${a.note}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          )}
        </div>

        {form && writable && (
          <footer className="drawer-foot">
            <button type="button" className="ghost" onClick={onClose} disabled={busy}>
              ปิด
            </button>
            {form.editable && (
              <button type="button" onClick={() => void save(false)} disabled={!canSave}>
                {dirty ? `บันทึกที่แก้ (${changed.length} แถว)` : 'ยังไม่มีอะไรแก้'}
              </button>
            )}
            {/* ยกเลิกใบ — ทำได้กับใบที่ยังไม่ยกเลิก/ปฏิเสธ (รวมใบที่อนุมัติแล้ว) */}
            {form.stage !== 'cancel' && form.stage !== 'reject' && (
              <button
                type="button"
                className="danger"
                onClick={() => void cancel()}
                disabled={busy || dirty}
                title={dirty ? 'บันทึกหรือยกเลิกการแก้ก่อน' : 'ยกเลิกใบนี้'}
              >
                ยกเลิกใบ
              </button>
            )}
            {form.actionable && (
              <>
                <button
                  type="button"
                  className="danger"
                  onClick={() => void act('REJECT')}
                  disabled={busy || dirty}
                  title={dirty ? 'บันทึกหรือยกเลิกการแก้ก่อน' : undefined}
                >
                  ไม่อนุมัติ
                </button>
                <button type="button" onClick={() => void act('APPROVE')} disabled={busy || dirty}>
                  {form.stage === 'wait_chain' ? 'อนุมัติ (ชั้นหัวหน้า)' : 'อนุมัติ'}
                </button>
              </>
            )}
          </footer>
        )}
      </aside>
    </>
  )
}
